import React, { useState, useRef, useEffect } from 'react'
import { Modal, Button, Flex, Input, Select, Form, Table, Spin } from 'antd'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { SERVICE_NAME } from 'service'
import { confirmDelete, confirmBase, errorTranslationMessage, EmptyData } from 'utils/ui'
import styles0 from 'pages/records/components/style.module.scss'
import { store as ReduxStore } from 'index'
import CategoriesService from 'api/Categories'
import { COLORS } from 'pages/records/components/RecordLabel/color'

import getTableInfo from 'pages/records/tableInfo/label'
import iconClear20SVG from '@/assets/images/icon-clear-20.svg'
import arrowIcon from '@/assets/images/chevron-down-24.svg'
import iconPlus24SVG from '@/assets/images/icon-plus-24.svg'
import iconSetting24SVG from '@/assets/images/icon-settings-24.svg'

const { Option } = Select
const { Search } = Input

const COLOR_OPTIONS = (t) =>
  COLORS.map((item) => ({
    value: item.value,
    label: t(item.label),
  }))

const LabelModal = ({
  id,
  labelList,
  open,
  setOpen,
  messageApi,
  labelReload,
  individualIdList,
  reload,
  reloadRecord,
}) => {
  const [form] = Form.useForm()
  const [isDisabled, setIsDisabled] = useState(true)
  const [loading, setLoading] = useState(false)
  const ref = useRef(null)
  const { t } = useTranslation()

  // 라벨 이름 및 색상 변경 시 버튼 활성 여부 결정
  const handleValuesChange = () => {
    const copiedValue = labelList.find((item) => item.id === id)

    const newName = form.getFieldValue('name') ?? copiedValue.name
    const newColor = form.getFieldValue('color') ?? copiedValue.color

    if (id && newName === copiedValue.name && newColor === copiedValue.color) {
      setIsDisabled(true)
    } else {
      setIsDisabled(false)
    }

    if (id == null) {
      if (form.getFieldValue('name') == null || form.getFieldValue('name').trim() === '') {
        setIsDisabled(true)
      } else {
        setIsDisabled(false)
      }
    }
  }

  // 라벨 생성
  const handleCreateLabel = async () => {
    try {
      setLoading(true)
      setOpen(false)

      const values = form.getFieldsValue()
      const res = await CategoriesService.post({
        name: values.name,
        color: values.color || COLORS[0].value,
      })

      if (res.code === 200) {
        labelReload()
      }
    } catch (error) {
      console.error(error)
      errorTranslationMessage(error.code, t, messageApi)
    } finally {
      setLoading(false)
    }
  }

  // 라벨 수정
  const confirmUpdateLabel = () => {
    confirmBase({
      title: t('record.label.confirmUpdateLabel'),
      content: t('record.label.confirmUpdateLabelContent', { service: t(SERVICE_NAME) }).replace(
        '<br/>',
        '\n',
      ),
      onOk: handleUpdateLabel,
      icon: 'red',
      width: 480,
    })
  }

  const handleUpdateLabel = async () => {
    setLoading(true)

    try {
      await CategoriesService.put(id, {
        name: ref.current?.getFieldValue('name'),
        color: ref.current?.getFieldValue('color') || COLORS[0].value,
      })

      labelReload()

      if (individualIdList.includes(id)) {
        reload()
        reloadRecord()
      }

      setOpen(false)
    } catch (error) {
      console.error(error)
      errorTranslationMessage(error.code, t, messageApi)
    } finally {
      setLoading(false)
    }
  }

  // 라벨 모달 닫기
  const handleCancel = () => {
    setOpen(false)
  }

  const handleMaxLength = (e) => {
    const { value } = e.target
    const trimmed = value.slice(0, 256)
    if (value.length > 256) {
      e.target.value = trimmed // 시각적으로도 잘림 적용
    }
  }

  // 라벨 모달 컨텐츠
  const content = (
    <div className={classNames(styles0.newLabelContainer)}>
      <Spin spinning={loading}>
        <div className={classNames(styles0.container)}>
          <span className={classNames(styles0.newLabelDesc)}>
            {t('record.label.requiredLabelName')}
          </span>
          <Form
            form={form}
            layout="vertical"
            className={classNames(styles0.newLabelForm)}
            ref={ref}
            onValuesChange={handleValuesChange}
            initialValues={{
              name: id ? labelList.find((item) => item.id === id)?.name : '',
              color: id ? labelList.find((item) => item.id === id)?.color : COLORS[0].value,
            }}
          >
            {/* name */}
            <Form.Item name="name" className={classNames(styles0.name)}>
              <Input
                placeholder={t('record.label.name')}
                onChange={handleMaxLength}
                maxLength={256}
              />
            </Form.Item>

            {/* color */}
            <Form.Item name="color" className={classNames(styles0.color)}>
              <Select
                popupClassName={classNames(styles0.colorPopup, 'customFieldDropdown')}
                suffixIcon={
                  <img src={arrowIcon} alt="icons" style={{ width: '24px', height: '24px' }} />
                }
                value={
                  id
                    ? COLORS.find(
                        (item) => item.value === labelList.find((value) => value.id === id)?.color,
                      )?.value
                    : COLORS[0].value
                }
              >
                {COLOR_OPTIONS(t).map((item) => (
                  <Option value={item.value} key={item.value}>
                    <span
                      className={classNames(styles0.circle)}
                      style={{
                        color: COLORS.find((color) => color.value === item.value)?.color,
                        fontSize: 16,
                      }}
                    >
                      ●
                    </span>
                    <span className={classNames(styles0.colorName)}>{item.label}</span>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </div>
      </Spin>
    </div>
  )

  return (
    <Modal
      title={id ? t('record.field.editLabel') : t('record.label.newLabel')}
      open={open}
      width={480}
      closeIcon={false}
      className={classNames('modalCustomContent', styles0.labelContent)}
      onCancel={handleCancel}
      okText={t('common.button.save')}
      cancelText={t('common.button.cancel')}
      okButtonProps={{
        className: classNames('type5', 'h38'),
        style: { minWidth: '100px' },
        disabled: isDisabled,
      }}
      cancelButtonProps={{
        className: classNames('h38'),
        style: { minWidth: '100px' },
      }}
      onOk={id ? confirmUpdateLabel : handleCreateLabel}
      wrapClassName={classNames('modalFooter')}
    >
      {content}
    </Modal>
  )
}

const RecordLabelModal = (props) => {
  const {
    labelOpen,
    setLabelOpen,
    reloadRecord,
    messageApi,
    manageLabelOpen,
    setManageLabelOpen,
    reload,
    individual,
  } = props
  const individualIdList = individual?.categories?.map((item) => item.id) || []

  const [loading, setLoading] = useState(false)
  const [labelList, setLabelList] = useState([])
  const labelIdList = labelList.map((item) => item.id)

  const [labelFormOpen, setLabelFormOpen] = useState(false)

  // 생성 및 편집 모달에 쓰이는 아이디
  const [id, setId] = useState(null)

  // 선택된 라벨 아이디 리스트
  const [selectedKeys, setSelectedKeys] = useState(individualIdList)
  const [disabled, setDisabled] = useState(false)

  const searchRef = useRef(null)
  const { t } = useTranslation()

  const loadData = async (searchText) => {
    setLoading(true)

    try {
      const request = searchText ? { search: searchText } : null
      const res = await CategoriesService.get(request)
      setLabelList(res.data)
      setLoading(false)
    } catch (error) {
      console.error(error)
    }
  }

  const labelReload = () => {
    loadData()
    ReduxStore.dispatch({
      type: 'menu/RELOAD',
    })
  }

  // individual에 라벨 추가
  const handleResourcePost = async () => {
    try {
      if (
        !individualIdList.every((value) => selectedKeys.includes(value)) ||
        !selectedKeys.every((value) => individualIdList.includes(value))
      ) {
        await CategoriesService.resourcePost(individual.id, {
          category_ids: selectedKeys,
        })
        reloadRecord()
        reload()
      }

      // 라벨 이름 변경 시 라벨 목록 리로드
      const individualList = individual.categories.map((item) => ({ id: item.id, name: item.name }))
      const selectedKeysList = labelList
        .filter((item) => selectedKeys.includes(item.id))
        .map((item) => ({
          id: item.id,
          name: item.name,
        }))

      const hasNameDiff = individualList.some((t1) => {
        const matchingTemp1 = selectedKeysList.find((t2) => t2.id === t1.id)
        return matchingTemp1 && t1.name !== matchingTemp1.name
      })

      if (hasNameDiff) {
        reload()
        reloadRecord()
      }

      setLabelOpen(false)
    } catch (error) {
      console.error(error)
    }
  }

  // 라벨 삭제
  const handleDeleteLabel = async (labelId) => {
    try {
      const res = await CategoriesService.delete(labelId)
      if (res.code === 200) {
        labelReload()
        reload()
        reloadRecord()
        const filteredList = selectedKeys.filter((item) => item !== labelId)
        setSelectedKeys(filteredList)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const confirmDeleteLabel = (key) => {
    confirmDelete({
      title: t('record.label.confirmDeleteLabel'),
      content: t('record.label.confirmDeleteLabelContent', { service: t(SERVICE_NAME) }),
      onOk: () => handleDeleteLabel(key),
      icon: 'red',
    })
  }

  // 라벨 모달 닫기
  const handleCancelModal = () => {
    console.log(individualIdList, labelIdList)
    if (manageLabelOpen && individualIdList.some((item) => !labelIdList.includes(item))) {
      reloadRecord()
      reload()
    }

    setLabelOpen(false)
    setManageLabelOpen(false)
    setLabelFormOpen(false)
    setId(null)
  }

  // 라벨 관리 모달 닫기
  const manageLabelSubmit = () => {
    setManageLabelOpen(false)
    if (individualIdList.some((item) => !labelIdList.includes(item))) {
      reloadRecord()
    }
  }

  // 라벨 모달 열기
  useEffect(() => {
    labelReload()
    if (individual?.categories) {
      setSelectedKeys(individualIdList || [])
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelOpen])

  useEffect(() => {
    const isSame =
      individualIdList.length === selectedKeys.length &&
      individualIdList.every((value) => selectedKeys.includes(value))

    setDisabled(isSame)
  }, [selectedKeys, individualIdList])

  // 라벨 검색
  const handleSearchSubmit = async (value) => {
    try {
      await loadData(value)
    } catch (error) {
      console.error(error)
    }
  }

  const searchAttrs = {
    placeholder: t('workspace.search.placeholder'),
    onSearch: handleSearchSubmit,
    ref: searchRef,
    suffix: null,
    allowClear: { clearIcon: <img src={iconClear20SVG} alt="clear" /> },
  }

  // table
  const tableInfo = getTableInfo(
    confirmDeleteLabel,
    selectedKeys,
    setSelectedKeys,
    setLabelFormOpen,
    setId,
  )
  const labelDataSource = labelList.map((item) => ({
    key: item.id,
    name: item.name,
    color: item.color,
  }))

  const tableAttrs = {
    loading,
    className: classNames(styles0.userTable),
    pagination: false,
    showSorterTooltip: false,
    bordered: false,
    showHeader: false,
    columns: tableInfo.columns(manageLabelOpen),
    locale: { emptyText: <EmptyData type="label" /> },
    dataSource: labelDataSource,
    rowKey: (record) => record.key,
    rowClassName: (record) =>
      !manageLabelOpen && selectedKeys.includes(record.key) ? styles0.selectedRow : '',
    // rowSelection: !manageLabelOpen
    //   ? {
    //       type: 'checkbox',
    //       checkStrictly: false,
    //       selectedRowKeys: selectedKeys,
    //       getCheckboxProps: (record) => ({
    //         disabled: selectedKeys.length >= 5 && selectedKeys.includes(record.key) === false,
    //       }),
    //       onSelect: (label, selected) => {
    //         if (
    //           selectedKeys.length >= 5 &&
    //           selectedKeys.includes(label.key) === false &&
    //           selected === true
    //         ) {
    //           return
    //         }

    //         let newSelectedKeys = [...selectedKeys]
    //         if (selected) {
    //           newSelectedKeys = [...selectedKeys, label.key]
    //         } else {
    //           newSelectedKeys = selectedKeys.filter((key) => key !== label.key)
    //         }

    //         setSelectedKeys(newSelectedKeys)
    //       },
    //     }
    //   : null,
    onRow: (label) => ({
      onClick: () => {
        if (manageLabelOpen) return

        if (selectedKeys.length >= 5 && !selectedKeys.includes(label.key)) {
          return
        }

        let newSelectedKeys = [...selectedKeys]
        if (selectedKeys.includes(label.key) === true) {
          newSelectedKeys = selectedKeys.filter((key) => key !== label.key)
        } else {
          newSelectedKeys = [...selectedKeys, label.key]
        }

        setSelectedKeys(newSelectedKeys)
      },
    }),
  }

  const labelContent = (
    <div className={classNames(styles0.recordLabelContainer)}>
      <Flex className={classNames(styles0.searchContainer)}>
        <Search {...searchAttrs} />

        <Button
          className={classNames(styles0.labelBtn)}
          icon={<img src={iconPlus24SVG} alt="plus" />}
          onClick={() => {
            setLabelFormOpen(true)
            setId(null)
          }}
        />

        {labelIdList.length > 0 && !manageLabelOpen ? (
          <Button
            className={classNames(styles0.labelBtn)}
            icon={<img src={iconSetting24SVG} alt="plus" />}
            onClick={() => setManageLabelOpen((prev) => !prev)}
          />
        ) : null}

        {labelFormOpen ? (
          // 생성 및 수정 모달
          <LabelModal
            id={id}
            labelList={labelList}
            open={labelFormOpen}
            setOpen={setLabelFormOpen}
            messageApi={messageApi}
            reload={reload}
            reloadRecord={reloadRecord}
            labelReload={labelReload}
            selectedKeys={selectedKeys}
            setSelectedKeys={setSelectedKeys}
            individualIdList={individualIdList}
          />
        ) : null}
      </Flex>

      <div className={classNames(styles0.labelList)}>
        <Table {...tableAttrs} />
      </div>
    </div>
  )

  // 라벨 모달 속성
  const modalProps = {
    title: manageLabelOpen ? t('record.label.manageLabel') : t('appConfig.menu.labelAs'),
    open: labelOpen,
    closeIcon: false,
    wrapClassName: classNames('om-popup-1'),
    forceRender: true,
    onCancel: handleCancelModal,
    className: classNames(styles0.labelModal),
    footer: (
      <div className={classNames('modalFooter')}>
        {!manageLabelOpen ? (
          <React.Fragment>
            <Flex>
              <Button onClick={handleCancelModal}>{t('common.button.cancel')}</Button>
              <Button
                className={classNames('type5', 'h38')}
                onClick={handleResourcePost}
                disabled={disabled}
              >
                {t('common.button.apply')}
              </Button>
            </Flex>
          </React.Fragment>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button className={classNames('type5', 'h38')} onClick={manageLabelSubmit}>
              {t('common.button.close')}
            </Button>
          </div>
        )}
      </div>
    ),
  }

  return <Modal {...modalProps}>{labelContent}</Modal>
}

export default RecordLabelModal
